import { useState, useEffect, useCallback } from 'react';
import { apiPost } from '../../api/axios';

export default function TodayHabits() {
  const [habits, setHabits] = useState([]);
  const [stats, setStats] = useState(null);
  const [today] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [todayRes, statsRes] = await Promise.all([
        apiPost({ action: 'habit_today' }),
        apiPost({
          action: 'habit_monthly_stats',
          params: {
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
          },
        }),
      ]);

      if (todayRes.flag === 'success') setHabits(todayRes.data);
      else setError(todayRes.msg || 'Failed to load habits');

      if (statsRes.flag === 'success') setStats(statsRes.data);
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleHabit = async (habitId, currentCompleted) => {
    const res = await apiPost({
      action: 'habit_toggle',
      habit_id: habitId,
      date: today,
    });
    if (res.flag === 'success') {
      setHabits(prev =>
        prev.map(h => (h.id === habitId ? { ...h, completed: res.data.completed } : h))
      );
      // Refresh stats after toggle
      const statsRes = await apiPost({
        action: 'habit_monthly_stats',
        params: {
          year: new Date().getFullYear(),
          month: new Date().getMonth() + 1,
        },
      });
      if (statsRes.flag === 'success') setStats(statsRes.data);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const completedToday = habits.filter(h => h.completed).length;
  const totalHabits = habits.length;
  const successRate = stats ? stats.successRate : 0;

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <svg className="animate-spin h-10 w-10 text-brand-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in-up">
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Today's Habits</h2>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl px-4 py-3">
          <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}

      {/* Hero Card */}
      <div className="bg-gradient-to-r from-brand-600 to-brand-500 rounded-3xl shadow-lg shadow-brand-500/25 p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold">{getGreeting()}</h3>
            <p className="text-sm opacity-80 mt-1">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
              })}
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
              <span className="text-2xl font-bold">{(successRate * 100).toFixed(0)}%</span>
            </div>
            <p className="text-xs mt-1 opacity-80">monthly</p>
          </div>
        </div>
        <div className="flex justify-between mt-6 text-sm">
          <span>Today: {completedToday}/{totalHabits}</span>
          <span>Monthly: {stats ? `${stats.totalCompleted}/${stats.totalGoals}` : '-'}</span>
        </div>
        <div className="w-full h-2 bg-white/20 rounded-full mt-2">
          <div
            className="h-2 bg-white rounded-full transition-all duration-500"
            style={{ width: `${(successRate * 100).toFixed(0)}%` }}
          />
        </div>
      </div>

      {/* Habits List */}
      {habits.length === 0 ? (
        <div className="bg-card-light dark:bg-card-dark backdrop-blur-xl rounded-3xl shadow-card-light dark:shadow-card-dark border border-white/20 dark:border-white/10 p-8 text-center">
          <svg className="w-12 h-12 mx-auto mb-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          <p className="text-slate-500 dark:text-slate-400">No habits yet. Create one in "My Habits".</p>
        </div>
      ) : (
        <div className="bg-card-light dark:bg-card-dark backdrop-blur-xl rounded-3xl shadow-card-light dark:shadow-card-dark border border-white/20 dark:border-white/10 p-6">
          <div className="space-y-1">
            {habits.map(habit => (
              <div
                key={habit.id}
                className="flex items-center gap-4 p-3 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
                onClick={() => toggleHabit(habit.id, habit.completed)}
              >
                <div
                  className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    habit.completed
                      ? 'bg-emerald-500 border-emerald-500'
                      : 'border-slate-300 dark:border-slate-600'
                  }`}
                >
                  {habit.completed && (
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${habit.completed ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-900 dark:text-white'}`}>
                    {habit.name}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{habit.goal}× this month</p>
                </div>
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400">
                  {habit.goal}×
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}